/* eslint-disable security/detect-object-injection --
 * Safe: object indexes in this file are all internal values, never user input.
 *   - `key` is a `StringKey` literal (union of dictionary keys in strings.ts)
 *   - `locale` and `DEFAULT_LOCALE` are `Locale` literals
 *   - `name` comes from `{var}` placeholders defined in our own strings.ts
 */
import { DEFAULT_LOCALE, type Locale } from "./locales";
import { type StringEntry, strings, type StringKey } from "./strings";

/**
 * Looks up a localized string by key.
 *
 * The dictionary schema requires a {@link DEFAULT_LOCALE} entry on every
 * key, so the lookup always resolves to a real string — there's no
 * key-as-fallback path. Supports `{varName}` placeholder interpolation;
 * missing vars are left untouched.
 *
 * @param key - Dotted string ID, e.g. `"address.postal_code.label"`
 * @param locale - Target locale
 * @param vars - Optional placeholder values
 * @returns The localized (and interpolated) string
 */
export function t(
  key: StringKey,
  locale: Locale,
  vars?: Record<string, string | number>,
): string {
  const entry: StringEntry = strings[key];
  const template = entry[locale] ?? entry[DEFAULT_LOCALE];
  return vars ? interpolate(template, vars) : template;
}

function interpolate(template: string, vars: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match,
  );
}
